<template>
  <section
    v-if="stages.length > 0"
    class="stage_diff rounded-lg border border-border bg-bg-surface p-3 shadow-sm"
    :aria-labelledby="headingId"
    :aria-busy="selectionPending || resetPending"
  >
    <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div class="min-w-0">
        <p class="font-mono text-2xs uppercase tracking-widest text-accent">{{ messages.stageDiff.eyebrow }}</p>
        <h2 :id="headingId" class="mt-0.5 text-base font-semibold text-text-primary">{{ title }}</h2>
        <p class="mt-0.5 text-xs text-text-muted">
          {{ messages.stageDiff.summary(viewModel.playerCount, viewModel.stageCount) }}
        </p>
      </div>
      <AppButton
        v-if="isOfficer"
        :variant="ButtonVariant.Danger"
        :size="ButtonSize.Sm"
        class="min-h-11! w-full disabled:cursor-not-allowed disabled:border-border! disabled:bg-bg-elevated! disabled:text-text-muted! disabled:opacity-100! disabled:shadow-none! sm:min-h-6! sm:w-auto"
        :disabled="selectionPending || resetPending || !hasResettableDiffs"
        @click="requestReset"
      >{{ resetButtonLabel }}</AppButton>
    </div>

    <p v-if="selectionPending" class="mt-3 font-mono text-xs text-accent" role="status">
      {{ messages.stage.loading }}
    </p>
    <p v-else-if="viewModel.playerCount === 0" class="mt-3 text-xs text-text-muted">
      {{ activeStageId === null ? messages.stageDiff.emptyAll : messages.stageDiff.emptyCurrent }}
    </p>
    <ul v-else class="mt-3 grid grid-cols-1 gap-2 xl:grid-cols-2" :aria-label="title">
      <li
        v-for="player in viewModel.players"
        :key="player.playerId"
        class="overflow-hidden rounded-md border border-border bg-bg-elevated"
      >
        <div class="flex flex-wrap items-center justify-between gap-1.5 border-b border-border bg-bg-surface px-2.5 py-2">
          <h3 class="text-sm font-semibold text-text-primary">{{ player.playerName }}</h3>
          <span class="font-mono text-2xs text-text-muted">
            {{ messages.stageDiff.playerStageSummary(player.stageChanges.length) }}
          </span>
        </div>

        <ul :aria-label="messages.stageDiff.playerChanges(player.playerName)" class="divide-y divide-border">
          <li
            v-for="change in player.stageChanges"
            :key="change.stageId"
            class="grid grid-cols-1 gap-2 px-2.5 py-2 sm:grid-cols-4 sm:items-start"
          >
            <div class="flex min-w-0 items-center sm:pt-1">
              <span
                class="max-w-full truncate rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-2xs font-semibold uppercase tracking-wide text-accent"
                :aria-label="messages.stageDiff.stageLabel(stageName(change))"
              >{{ stageName(change) }}</span>
            </div>

            <div class="flex min-w-0 flex-col gap-1.5 sm:col-span-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div
                v-if="change.placement !== null"
                class="flex min-w-0 flex-wrap items-center gap-1 rounded-sm border border-border/70 bg-bg-surface px-1.5 py-1"
                :aria-label="messages.stageDiff.moveChange(placementBefore(change), placementAfter(change))"
              >
                <span class="font-mono text-2xs uppercase tracking-wide text-text-muted">{{ messages.stageDiff.move }}</span>
                <span class="truncate text-xs text-text-secondary">{{ placementBefore(change) }}</span>
                <span class="text-accent" aria-hidden="true">→</span>
                <span class="truncate text-xs font-medium text-text-primary">{{ placementAfter(change) }}</span>
              </div>

              <div
                v-if="change.hasBuildChange"
                class="flex min-w-0 flex-wrap items-center gap-1 rounded-sm border border-border/70 bg-bg-surface px-1.5 py-1"
                :aria-label="messages.stageDiff.buildChange"
              >
                <span class="font-mono text-2xs uppercase tracking-wide text-text-muted">{{ messages.stageDiff.build }}</span>
                <span
                  class="inline-flex min-w-0 items-center gap-1 rounded-sm border border-border bg-bg-elevated px-1 py-0.5"
                  role="group"
                  :aria-label="buildSideLabel(messages.stageDiff.sourceBuild, change.sourceBuild)"
                >
                  <template v-if="change.sourceBuild !== null">
                    <span class="font-mono text-2xs text-text-secondary">{{ messages.stageDiff.buildNumber(change.sourceBuild.orderIndex + 1) }}</span>
                    <span v-if="change.sourceBuild.weaponSlugs.length === 0" class="text-2xs text-text-muted">{{ messages.stageDiff.emptyWeapons }}</span>
                    <img
                      v-for="(slug, weaponIndex) in change.sourceBuild.weaponSlugs"
                      :key="`source:${change.stageId}:${weaponIndex}:${slug}`"
                      :src="weaponIcon(slug)"
                      :alt="weaponName(slug)"
                      class="h-6 w-6 shrink-0 rounded-sm border border-border object-cover"
                      draggable="false"
                    />
                  </template>
                  <span v-else class="text-2xs text-text-muted">{{ messages.stageDiff.emptyBuild }}</span>
                </span>
                <span class="text-accent" aria-hidden="true">→</span>
                <span
                  class="inline-flex min-w-0 items-center gap-1 rounded-sm border border-accent/40 bg-accent/10 px-1 py-0.5"
                  role="group"
                  :aria-label="buildSideLabel(messages.stageDiff.targetBuild, change.targetBuild)"
                >
                  <template v-if="change.targetBuild !== null">
                    <span class="font-mono text-2xs text-accent">{{ messages.stageDiff.buildNumber(change.targetBuild.orderIndex + 1) }}</span>
                    <span v-if="change.targetBuild.weaponSlugs.length === 0" class="text-2xs text-text-muted">{{ messages.stageDiff.emptyWeapons }}</span>
                    <img
                      v-for="(slug, weaponIndex) in change.targetBuild.weaponSlugs"
                      :key="`target:${change.stageId}:${weaponIndex}:${slug}`"
                      :src="weaponIcon(slug)"
                      :alt="weaponName(slug)"
                      class="h-6 w-6 shrink-0 rounded-sm border border-border object-cover"
                      draggable="false"
                    />
                  </template>
                  <span v-else class="text-2xs text-text-muted">{{ messages.stageDiff.emptyBuild }}</span>
                </span>
              </div>

              <div
                v-if="change.addedTags.length > 0 || change.removedTags.length > 0"
                class="flex min-w-0 flex-wrap items-center gap-1 rounded-sm border border-border/70 bg-bg-surface px-1.5 py-1"
                role="group"
                :aria-label="messages.stageDiff.tags"
              >
                <span class="font-mono text-2xs uppercase tracking-wide text-text-muted">{{ messages.stageDiff.tags }}</span>
                <span
                  v-for="tag in change.addedTags"
                  :key="`added:${change.stageId}:${tag.id}`"
                  class="inline-flex items-center rounded-full px-1.5 py-0.5 text-2xs font-semibold"
                  :class="tagChipClassFor({ color: tag.color, active: false })"
                  :aria-label="messages.stageDiff.addedTag(tagDisplayName(tag.name))"
                >+ {{ tagDisplayName(tag.name) }}</span>
                <span
                  v-for="tag in change.removedTags"
                  :key="`removed:${change.stageId}:${tag.id}`"
                  class="inline-flex items-center rounded-full px-1.5 py-0.5 text-2xs font-semibold line-through opacity-75"
                  :class="tagChipClassFor({ color: tag.color, active: false })"
                  :aria-label="messages.stageDiff.removedTag(tagDisplayName(tag.name))"
                >− {{ tagDisplayName(tag.name) }}</span>
              </div>
            </div>
          </li>
        </ul>
      </li>
    </ul>

    <p v-if="resetError !== null" class="mt-2 text-xs text-danger" role="alert">{{ resetError }}</p>

    <AppModal
      :model-value="confirmationOpen"
      :title="confirmationTitle"
      size="sm"
      :close-on-outside-click="!resetPending"
      :close-on-esc="!resetPending"
      @update:model-value="onConfirmationUpdate"
    >
      <p class="text-sm leading-relaxed text-text-secondary">{{ confirmationBody }}</p>
      <p v-if="resetError !== null" class="mt-2 text-xs text-danger" role="alert">{{ resetError }}</p>
      <template #footer>
        <div class="flex flex-wrap justify-end gap-2">
          <AppButton class="min-h-11! min-w-11! sm:min-h-6! sm:min-w-0!" :variant="ButtonVariant.Secondary" :disabled="resetPending" @click="closeConfirmation">
            {{ messages.stageDiff.cancel }}
          </AppButton>
          <AppButton class="min-h-11! min-w-11! disabled:cursor-not-allowed disabled:border-border! disabled:bg-bg-elevated! disabled:text-text-muted! disabled:opacity-100! disabled:shadow-none! sm:min-h-6! sm:min-w-0!" :variant="ButtonVariant.Danger" :disabled="resetPending" @click="confirmReset">
            {{ resetPending ? messages.stageDiff.resetting : messages.stageDiff.confirm }}
          </AppButton>
        </div>
      </template>
    </AppModal>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { WEAPONS_BY_SLUG, weaponIcon } from '@/data/weapons'
import { tagChipClassFor, tagDisplayName } from '@/features/gvg-planner/composables'
import { useGvgPlannerStore } from '@/features/gvg-planner/stores/gvgPlannerStore'
import { RaidStageKind } from '@/features/gvg-planner/types'
import type { RaidStage, RaidStageBuildSnapshot, RaidStageDiff, RaidStageId } from '@/features/gvg-planner/types'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { defineLocaleCatalog } from '@/shared/i18n'
import { AppButton, AppModal, ButtonSize, ButtonVariant } from '@/shared/ui'
import { calendarEn } from './locales/en.ts'
import { calendarRu } from './locales/ru.ts'
import { createStageDiffViewModel } from './stageDiffViewModel.ts'
import type { StageDiffStageView } from './stageDiffViewModel.ts'
import { useStageDiffReset } from './useStageDiffReset.ts'

export interface StageDiffCardProps {
  readonly diffs: ReadonlyArray<RaidStageDiff>
  readonly stages: ReadonlyArray<RaidStage>
  readonly activeStageId: RaidStageId | null
  readonly isOfficer: boolean
  readonly selectionPending: boolean
}

const props = defineProps<StageDiffCardProps>()
const emit = defineEmits<{
  (event: 'reset', scope: 'current' | 'all'): void
}>()
const messages = useLocaleMessages(defineLocaleCatalog({ en: calendarEn, ru: calendarRu }))
const store = useGvgPlannerStore()
const headingId = 'raid-stage-differences-heading'
const resetController = useStageDiffReset({
  reset: (scope) => scope === 'all'
    ? store.resetAllStageChanges()
    : store.resetStageChanges(),
  currentError: () => store.error,
  fallbackError: () => messages.value.stageDiff.resetError,
})
const {
  confirmationOpen,
  resetPending,
  resetError,
  updateConfirmation: onConfirmationUpdate,
  closeConfirmation,
} = resetController

const viewModel = computed(() => createStageDiffViewModel(
  props.diffs,
  props.stages,
  props.activeStageId,
))
const hasResettableDiffs = computed<boolean>(() => props.activeStageId === null
  ? props.diffs.length > 0
  : props.diffs.some((diff) => diff.stageId === props.activeStageId),
)
const activeStage = computed<RaidStage | undefined>(() =>
  props.stages.find((stage) => stage.id === props.activeStageId),
)
const title = computed<string>(() =>
  props.activeStageId === null
    ? messages.value.stageDiff.titleAll
    : messages.value.stageDiff.titleCurrent(stageDisplayName(activeStage.value, props.activeStageId)),
)
const resetButtonLabel = computed<string>(() =>
  props.activeStageId === null
    ? messages.value.stageDiff.resetAll
    : messages.value.stageDiff.resetCurrent,
)
const confirmationTitle = computed<string>(() =>
  props.activeStageId === null
    ? messages.value.stageDiff.confirmAllTitle
    : messages.value.stageDiff.confirmCurrentTitle,
)
const confirmationBody = computed<string>(() =>
  props.activeStageId === null
    ? messages.value.stageDiff.confirmAllBody
    : messages.value.stageDiff.confirmCurrentBody,
)

function stageName(change: StageDiffStageView): string {
  if (change.stageName.trim()) return change.stageName.trim()
  const stage = props.stages.find((candidate) => candidate.id === change.stageId)
  return stageDisplayName(stage, change.stageId)
}

function stageDisplayName(stage: RaidStage | undefined, fallback: string): string {
  if (stage === undefined) return fallback
  if (stage.name.trim()) return stage.name.trim()
  if (stage.kind === RaidStageKind.Forest) return messages.value.stage.forest
  if (stage.kind === RaidStageKind.MidPush) return messages.value.stage.towers
  if (stage.kind === RaidStageKind.LateDefense) return messages.value.stage.goose
  if (stage.kind === RaidStageKind.Endgame) return messages.value.stage.tree
  if (stage.kind === RaidStageKind.Opening) return messages.value.stage.opening
  return messages.value.stage.custom
}

function placementBefore(change: StageDiffStageView): string {
  const placement = change.placement
  if (placement === null || placement.beforeGroupId === null) return messages.value.stageDiff.unassigned
  return placement.beforeGroupName
}

function placementAfter(change: StageDiffStageView): string {
  return change.placement?.afterGroupName ?? messages.value.stageDiff.unassigned
}

function buildSnapshotLabel(snapshot: RaidStageBuildSnapshot): string {
  const weapons = snapshot.weaponSlugs.map(weaponName).join(', ') || messages.value.stageDiff.emptyWeapons
  return messages.value.stageDiff.buildSnapshot(snapshot.orderIndex + 1, weapons)
}

function buildSideLabel(side: string, snapshot: RaidStageBuildSnapshot | null): string {
  return snapshot === null
    ? `${side}: ${messages.value.stageDiff.emptyBuild}`
    : `${side}: ${buildSnapshotLabel(snapshot)}`
}

function weaponName(slug: string): string {
  return WEAPONS_BY_SLUG.get(slug)?.name ?? slug
}

function requestReset(): void {
  resetController.requestReset(
    props.isOfficer && !props.selectionPending && hasResettableDiffs.value,
  )
}

async function confirmReset(): Promise<void> {
  if (!props.isOfficer) return
  const scope = props.activeStageId === null ? 'all' : 'current'
  if (await resetController.confirm(scope)) emit('reset', scope)
}
</script>
